import React from "react";
import { Link } from "react-router-dom";
import { FiAlertTriangle, FiAlertOctagon, FiInfo, FiCheckCircle, FiXCircle, FiClock, FiCheck, FiX, FiHeart, FiLoader, FiCalendar, FiArrowRight, FiThumbsUp, FiThumbsDown } from "react-icons/fi";
import { Columns, Donut, Fact, HBars, LineChart, Meter, Panel, StackBar, SubTitle } from "./charts";
import { ENTITY, INK, PALETTE, RAMP, STATUS, foldSlices, money, percent, sum, timeAgo } from "./chartUtils";

const colorFor = (rows, map, fallbackColors = PALETTE) => (rows || []).map((r, i) => ({ ...r, color: map[r.key] || fallbackColors[i % fallbackColors.length] }));
const icon = (Icon, color) => <Icon aria-hidden="true" size={12} style={{ color }} />;

// ---- the big chart: everything over time ----------------------------------------------------------------------------------------------------------
export function OverviewChart({ data }) {
  const series = [
    { key: "ai", label: "AI tool runs", color: ENTITY.ai, values: data.ai.series },
    { key: "chat", label: "Chatbot answers", color: ENTITY.chat, values: data.chatbots.series },
    { key: "inbox", label: "Enquiries", color: ENTITY.inbox, values: data.inbox.series },
    { key: "cowork", label: "New Co-Work members", color: ENTITY.cowork, values: data.cowork.series_members },
    { key: "foundation", label: "Help requests", color: ENTITY.foundation, values: data.foundation.series_cases },
  ];
  return (
    <Panel title="Activity over time" subtitle={`Every day of the last ${data.range.days} days. Click a name to hide or show its line (chatbot answers are much more numerous, so they start hidden).`} className="h-full">
      <LineChart dates={data.range.dates} series={series} title="Activity over time" startHidden={["chat"]} />
    </Panel>
  );
}

const TONES = { warn: [FiAlertTriangle, STATUS.warning, "Needs you"], bad: [FiAlertOctagon, STATUS.critical, "Problem"], info: [FiInfo, PALETTE[0], "For your information"] };

export function AttentionPanel({ items }) {
  const total = sum(items.map((i) => i.count));
  return (
    <Panel title="Needs your attention" subtitle={items.length ? `${total} ${total === 1 ? "thing" : "things"} waiting` : "Nothing is waiting for you"} className="h-full">
      {!items.length ? (
        <div className="flex items-center gap-3 rounded-xl bg-white/5 px-4 py-5 text-sm"><FiCheckCircle size={22} style={{ color: STATUS.good }} aria-hidden="true" /><span><b>All clear.</b> <span className="text-gray-400">No messages, requests or problems are waiting.</span></span></div>
      ) : (
        <ul className="space-y-2">
          {items.map((i) => {
            const [Icon, color, word] = TONES[i.tone] || TONES.info;
            return (
              <li key={i.key}>
                <Link to={i.to} className="flex items-center gap-3 rounded-xl bg-white/5 hover:bg-white/10 px-3.5 py-2.5 transition-colors">
                  <Icon aria-hidden="true" style={{ color }} className="shrink-0" /><span className="sr-only">{word}: </span>
                  <span className="text-sm text-gray-200 flex-1 min-w-0">{i.label}</span>
                  <span className="text-sm font-bold tabular-nums px-2 py-0.5 rounded-full bg-black/30">{i.count}</span><FiArrowRight aria-hidden="true" className="text-gray-500" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

const FEED_COLOR = { contact: ENTITY.inbox, quote: ENTITY.inbox, foundation: ENTITY.foundation, offer: ENTITY.foundation, member: ENTITY.cowork, job: ENTITY.cowork, lead: ENTITY.chat, ticket: ENTITY.chat, post: ENTITY.blogs };

export function ActivityPanel({ items }) {
  return (
    <Panel title="Latest activity" subtitle="New messages, requests, members, leads and posts" className="h-full">
      {!items.length ? <p className="text-sm text-gray-500">Nothing has happened yet.</p> : (
        <ul className="divide-y divide-navy-border">
          {items.map((a, i) => (
            <li key={`${a.type}-${i}`}>
              <Link to={a.to} className="flex items-start gap-3 py-2.5 hover:bg-white/5 rounded-lg px-2 -mx-2 transition-colors">
                <span aria-hidden="true" className="w-2.5 h-2.5 rounded-full mt-1.5 shrink-0" style={{ background: FEED_COLOR[a.type] || INK.muted }} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm truncate" dir="auto">{a.title}</span>
                  <span className="block text-xs text-gray-500 truncate" dir="auto"><span className="text-gray-400">{a.label}</span>{a.detail ? ` - ${a.detail}` : ""}</span>
                </span>
                <span className="text-[11px] text-gray-500 whitespace-nowrap mt-0.5">{timeAgo(a.at)}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

export function HealthPanel({ items }) {
  return (
    <Panel title="System health" subtitle="Things that keep the site and the chatbots working" className="h-full">
      <ul className="space-y-2.5">
        {items.map((h) => {
          const [Icon, color, word] = h.ok ? [FiCheckCircle, STATUS.good, "Good"] : h.warn ? [FiAlertTriangle, STATUS.warning, "Warning"] : [FiXCircle, STATUS.critical, "Problem"];
          const row = (
            <div className="flex items-start gap-3 rounded-xl bg-white/5 px-3.5 py-2.5">
              <Icon aria-hidden="true" style={{ color }} className="mt-0.5 shrink-0" /><span className="sr-only">{word}: </span>
              <div className="min-w-0"><div className="text-sm text-gray-200">{h.label}</div><div className="text-xs text-gray-400">{h.detail}</div></div>
            </div>
          );
          return <li key={h.key}>{h.to ? <Link to={h.to} className="block hover:opacity-90">{row}</Link> : row}</li>;
        })}
      </ul>
    </Panel>
  );
}

// ---- AI tools ---------------------------------------------------------------------------------------------------------------------------------------------------
const JOB_STATUS = { completed: [STATUS.good, FiCheck], failed: [STATUS.critical, FiX], queued: [INK.muted, FiClock], processing: [PALETTE[0], FiLoader] };

export function AISection({ data }) {
  const a = data.ai;
  const status = (a.by_status || []).map((s) => { const [color, Icon] = JOB_STATUS[s.key] || [INK.muted, FiClock]; return { ...s, color, icon: icon(Icon, color) }; });
  return (
    <Panel id="section-ai" title="AI tools" subtitle="Runs of the tools on the AI Tools pages" accent={ENTITY.ai} to="/admin/pages/ai-tools/categories" linkText="Manage AI tools">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <Fact label="Runs in this period" value={a.runs} color={ENTITY.ai} />
        <Fact label="Tools used" value={a.tools_used} />
        <Fact label="Running now" value={a.running_now} />
        <Fact label="Failed" value={a.failed} hint={a.runs ? `${percent(a.failed, a.runs)}% of runs` : ""} />
        <Fact label="Average time" value={a.avg_seconds ? (a.avg_seconds >= 60 ? `${Math.round(a.avg_seconds / 60)} min` : `${a.avg_seconds} s`) : "-"} hint="for finished jobs" />
      </div>
      <div className="grid lg:grid-cols-2 gap-8">
        <div><SubTitle>Runs per day</SubTitle><Columns dates={data.range.dates} values={a.series} color={ENTITY.ai} label="runs" /></div>
        <div><SubTitle>Most used tools</SubTitle><HBars rows={a.by_kind.slice(0, 8)} color={ENTITY.ai} empty="No AI tool was used in this period." /></div>
      </div>
      <div className="mt-6"><SubTitle>How the runs ended</SubTitle><StackBar parts={status} empty="No jobs in this period." /></div>
    </Panel>
  );
}

// ---- chatbots, leads, support, WhatsApp ------------------------------------------------------------------------------------------------------
const SOURCE_COLORS = { widget: PALETTE[0], api: PALETTE[1], whatsapp: PALETTE[2], test: PALETTE[3] };
const TICKET_COLORS = { open: [STATUS.warning, FiClock], pending: [PALETTE[0], FiLoader], resolved: [STATUS.good, FiCheck] };

export function ChatbotsSection({ data }) {
  const c = data.chatbots;
  const grade = [{ key: "cold", label: "Cold", color: RAMP[0] }, { key: "warm", label: "Warm", color: RAMP[1] }, { key: "hot", label: "Hot", color: RAMP[2] }].map((g) => ({ ...g, count: (c.leads_by_grade.find((x) => x.key === g.key) || { count: 0 }).count }));
  const hotWarm = grade[1].count + grade[2].count;
  const funnel = [{ key: "c", label: "Conversations", count: c.conversations, color: RAMP[0] }, { key: "l", label: "Leads", count: c.leads, color: RAMP[1] }, { key: "q", label: "Warm or hot", count: hotWarm, color: RAMP[2] }, { key: "w", label: "Won", count: c.leads_won, color: RAMP[3] }];
  const tickets = (c.tickets_by_status || []).map((t) => { const [color, Icon] = TICKET_COLORS[t.key] || [INK.muted, FiClock]; return { ...t, color, icon: icon(Icon, color) }; });
  const rating = [{ key: "h", label: "Helpful", count: c.helpful, color: STATUS.good, icon: icon(FiThumbsUp, STATUS.good) }, { key: "n", label: "Not helpful", count: c.not_helpful, color: STATUS.serious, icon: icon(FiThumbsDown, STATUS.serious) }];
  return (
    <Panel id="section-chatbots" title="Chatbots, leads and support" subtitle="Website chat, client API and WhatsApp answered by the AI assistants" accent={ENTITY.chat} to="/admin/pages/ai-tools/chatbots" linkText="Open AI Chatbots">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Fact label="Chatbots" value={c.bots} hint={`${c.active_bots} switched on`} color={ENTITY.chat} />
        <Fact label="Answers" value={c.answers} />
        <Fact label="Conversations" value={c.conversations} />
        <Fact label="New leads" value={c.leads} hint={`${c.leads_new} not handled`} />
        <Fact label="Won value" value={money(c.won_value)} hint={`${c.leads_won} deals won`} />
        <Fact label="Open tickets" value={c.tickets_open} hint={`${c.whatsapp_contacts} WhatsApp contacts`} />
      </div>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2"><SubTitle>Answers per day</SubTitle><Columns dates={data.range.dates} values={c.series} color={ENTITY.chat} label="answers" /></div>
        <div><SubTitle>Where the questions came from</SubTitle><Donut slices={colorFor(c.by_source, SOURCE_COLORS)} centerLabel="answers" size={120} empty="No answers in this period." /></div>
      </div>
      <div className="grid lg:grid-cols-3 gap-8 mt-8">
        <div><SubTitle>From chat to customer</SubTitle><HBars rows={funnel} empty="No conversations yet." /></div>
        <div className="space-y-6">
          <div><SubTitle>How likely the leads are to buy</SubTitle><StackBar parts={grade} empty="No leads in this period." /></div>
          <div><SubTitle>Support tickets</SubTitle><StackBar parts={tickets} empty="No tickets yet." /></div>
        </div>
        <div className="space-y-6">
          <div><SubTitle>Did the answers help?</SubTitle><StackBar parts={rating} empty="Customers have not rated answers in this period." /></div>
          <div>
            <SubTitle>Booked meetings coming up</SubTitle>
            {!c.meetings_upcoming.length ? <p className="text-sm text-gray-500">No meetings are booked.</p> : (
              <ul className="space-y-2">{c.meetings_upcoming.map((m) => <li key={m.id} className="flex items-center gap-2.5 text-sm"><FiCalendar aria-hidden="true" className="text-gray-400 shrink-0" /><span className="tabular-nums text-gray-200 whitespace-nowrap">{new Date(m.start).toLocaleString([], { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span><span className="text-gray-400 truncate">{m.who || m.bot}</span></li>)}</ul>
            )}
          </div>
        </div>
      </div>
    </Panel>
  );
}

// ---- Co-Work ------------------------------------------------------------------------------------------------------------------------------------------------------
const ROLE_COLORS = { buyer: PALETTE[0], seller: PALETTE[1] };
const JOB_COLORS = { open: [STATUS.good, FiCheck], in_progress: [PALETTE[0], FiLoader], completed: [PALETTE[2], FiCheck], closed: [INK.muted, FiX] };
const PROPOSAL_COLORS = { pending: [STATUS.warning, FiClock], accepted: [STATUS.good, FiCheck], rejected: [STATUS.critical, FiX], withdrawn: [INK.muted, FiX] };
const withStatus = (rows, map) => (rows || []).map((r) => { const [color, Icon] = map[r.key] || [INK.muted, FiClock]; return { ...r, color, icon: icon(Icon, color) }; });

export function CoWorkSection({ data }) {
  const w = data.cowork;
  const series = [{ key: "m", label: "New members", color: PALETTE[0], values: w.series_members }, { key: "j", label: "New jobs", color: PALETTE[1], values: w.series_jobs }, { key: "p", label: "New proposals", color: PALETTE[2], values: w.series_proposals }];
  return (
    <Panel id="section-cowork" title="Co-Work marketplace" subtitle="Buyers, sellers, jobs and proposals" accent={ENTITY.cowork} to="/admin/pages/co-work/members" linkText="Open Co-Work">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Fact label="Members" value={w.members} hint={`${w.members_new} joined in this period`} color={ENTITY.cowork} />
        <Fact label="Open jobs" value={w.jobs_open} hint={`${w.jobs} jobs in total`} />
        <Fact label="Jobs posted" value={w.jobs_new} hint="in this period" />
        <Fact label="Proposals" value={w.proposals} hint={`${w.proposals_new} new`} />
        <Fact label="Conversations" value={w.conversations} hint="between members" />
        <Fact label="Reviews" value={w.reviews} hint={`${w.ai_questions} AI helper questions`} />
      </div>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2"><SubTitle>New members, jobs and proposals</SubTitle><LineChart dates={data.range.dates} series={series} height={230} title="Co-Work activity" /></div>
        <div><SubTitle>Buyers and sellers</SubTitle><Donut slices={colorFor(w.by_role, ROLE_COLORS)} centerLabel="members" size={120} empty="No members yet." /></div>
      </div>
      <div className="grid lg:grid-cols-3 gap-8 mt-8">
        <div><SubTitle>Jobs by status</SubTitle><StackBar parts={withStatus(w.jobs_by_status, JOB_COLORS)} empty="No jobs yet." /><div className="mt-6"><SubTitle>Proposals by status</SubTitle><StackBar parts={withStatus(w.proposals_by_status, PROPOSAL_COLORS)} empty="No proposals yet." /></div></div>
        <div><SubTitle>Job categories</SubTitle><HBars rows={w.jobs_by_category} color={ENTITY.cowork} empty="No categories yet." /></div>
        <div><SubTitle>Members by country</SubTitle><HBars rows={w.countries} color={ENTITY.cowork} empty="No countries yet." /></div>
      </div>
    </Panel>
  );
}

// ---- Foundation -----------------------------------------------------------------------------------------------------------------------------------------------
const CASE_COLORS = { pending: [STATUS.warning, FiClock], approved: [STATUS.good, FiCheck], rejected: [STATUS.critical, FiX], fulfilled: [PALETTE[0], FiHeart] };
const OFFER_COLORS = { pending: [STATUS.warning, FiClock], confirmed: [STATUS.good, FiCheck], declined: [STATUS.critical, FiX] };

export function FoundationSection({ data }) {
  const f = data.foundation;
  const series = [{ key: "c", label: "Help requests", color: PALETTE[0], values: f.series_cases }, { key: "o", label: "Help offers", color: PALETTE[1], values: f.series_offers }];
  const top = Math.max(f.needed, f.pledged, f.confirmed, 1);
  return (
    <Panel id="section-foundation" title="Foundation" subtitle="People asking for help, and people offering it" accent={ENTITY.foundation} to="/admin/pages/foundation/requests" linkText="Open Foundation">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Fact label="Help requests" value={f.cases} hint={`${f.cases_new} in this period`} color={ENTITY.foundation} />
        <Fact label="To review" value={f.pending} hint="waiting for you" />
        <Fact label="Help offers" value={f.offers} hint={`${f.offers_new} in this period`} />
        <Fact label="Offers to confirm" value={f.offers_pending} />
        <Fact label="Money needed" value={money(f.needed)} hint="approved requests" />
        <Fact label="Money offered" value={money(f.pledged)} hint={`${money(f.confirmed)} received`} />
      </div>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2"><SubTitle>Requests and offers</SubTitle><LineChart dates={data.range.dates} series={series} height={230} title="Foundation activity" /></div>
        <div className="space-y-5">
          <SubTitle>Money</SubTitle>
          <Meter label="Needed (approved requests)" value={f.needed} max={top} color={PALETTE[0]} format={money} />
          <Meter label="Offered" value={f.pledged} max={top} color={PALETTE[2]} format={money} right={f.needed ? `${percent(f.pledged, f.needed)}% of needed` : ""} />
          <Meter label="Received and confirmed" value={f.confirmed} max={top} color={PALETTE[3]} format={money} />
        </div>
      </div>
      <div className="grid lg:grid-cols-3 gap-8 mt-8">
        <div><SubTitle>Requests by status</SubTitle><StackBar parts={withStatus(f.by_status, CASE_COLORS)} empty="No requests yet." /><div className="mt-6"><SubTitle>Offers by status</SubTitle><StackBar parts={withStatus(f.offers_by_status, OFFER_COLORS)} empty="No offers yet." /></div></div>
        <div><SubTitle>What people ask for</SubTitle><HBars rows={f.by_category} color={ENTITY.foundation} empty="No requests yet." /></div>
        <div><SubTitle>How people offer to help</SubTitle><Donut slices={foldSlices(f.offers_by_type)} centerLabel="offers" size={110} empty="No offers yet." /></div>
      </div>
    </Panel>
  );
}

// ---- Blogs ----------------------------------------------------------------------------------------------------------------------------------------------------------
export function BlogsSection({ data }) {
  const b = data.blogs;
  return (
    <Panel id="section-blogs" title="Blogs" subtitle="Articles in the four blog categories" accent={ENTITY.blogs} to="/admin/pages/blogs/posts" linkText="Open Blogs">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <Fact label="Posts" value={b.posts} color={ENTITY.blogs} />
        <Fact label="Published" value={b.published} />
        <Fact label="Drafts" value={b.drafts} hint="not visible yet" />
        <Fact label="Scheduled" value={b.scheduled} hint="go live later" />
        <Fact label="Published in this period" value={b.new} />
      </div>
      <div className="grid lg:grid-cols-3 gap-8">
        <div><SubTitle>Posts published per day</SubTitle><Columns dates={data.range.dates} values={b.series} color={ENTITY.blogs} label="posts" /></div>
        <div><SubTitle>Posts by category</SubTitle><HBars rows={b.by_category} color={ENTITY.blogs} empty="No posts yet." /></div>
        <div>
          <SubTitle>Latest posts</SubTitle>
          {!b.latest.length ? <p className="text-sm text-gray-500">No posts yet.</p> : (
            <ul className="space-y-2.5">{b.latest.map((p) => <li key={p.id}><Link to="/admin/pages/blogs/posts" className="flex items-start gap-2 text-sm hover:text-white"><span className="min-w-0 flex-1"><span className="block truncate text-gray-200" dir="auto">{p.title}</span><span className="block text-[11px] text-gray-500">{p.category}</span></span><span className={`text-[10px] px-2 py-0.5 rounded-full mt-0.5 ${p.published ? "bg-emerald-500/15 text-emerald-200" : "bg-amber-500/15 text-amber-200"}`}>{p.published ? "Published" : "Draft"}</span></Link></li>)}</ul>
          )}
        </div>
      </div>
    </Panel>
  );
}

// ---- the inbox and the website itself ------------------------------------------------------------------------------------------------------------
export function InboxSection({ data }) {
  const i = data.inbox;
  return (
    <Panel id="section-inbox" title="Enquiries" subtitle="Contact page messages and quote requests" accent={ENTITY.inbox} to="/admin/pages/contact/messages" linkText="Open messages" className="h-full">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Fact label="Enquiries" value={i.enquiries} color={ENTITY.inbox} />
        <Fact label="Messages" value={i.messages} />
        <Fact label="Quote requests" value={i.quotes} />
        <Fact label="Unread" value={i.unread} hint="all time" />
      </div>
      <SubTitle>Enquiries per day</SubTitle>
      <Columns dates={data.range.dates} values={i.series} color={ENTITY.inbox} label="enquiries" height={150} />
      <div className="mt-6"><SubTitle>What quote requests are about</SubTitle><HBars rows={i.top_services} color={ENTITY.inbox} empty="No quote requests in this period." /></div>
    </Panel>
  );
}

export function WebsiteSection({ data }) {
  const w = data.website;
  return (
    <Panel id="section-website" title="Website content" subtitle="What is switched on, out of everything you have" accent={PALETTE[0]} to="/admin/pages/home" linkText="Edit pages" className="h-full">
      <div className="space-y-5">
        <Meter label="Menu items switched on" value={w.active_menu_items} max={w.menu_items} color={PALETTE[0]} right={`of ${w.menu_items}`} />
        <Meter label="Services shown" value={w.active_services} max={w.services} color={PALETTE[0]} right={`of ${w.services}`} />
        <Meter label="Portfolio projects shown" value={w.active_projects} max={w.projects} color={PALETTE[0]} right={`of ${w.projects}`} />
      </div>
      <div className="grid grid-cols-2 gap-3 mt-6">
        <Link to="/admin/pages/home/services" className="rounded-xl bg-white/5 hover:bg-white/10 px-3.5 py-3 text-sm flex items-center justify-between">Services <FiArrowRight aria-hidden="true" className="text-gray-500" /></Link>
        <Link to="/admin/pages/portfolio/projects" className="rounded-xl bg-white/5 hover:bg-white/10 px-3.5 py-3 text-sm flex items-center justify-between">Portfolio <FiArrowRight aria-hidden="true" className="text-gray-500" /></Link>
        <Link to="/admin/settings/site-identity" className="rounded-xl bg-white/5 hover:bg-white/10 px-3.5 py-3 text-sm flex items-center justify-between">Site identity <FiArrowRight aria-hidden="true" className="text-gray-500" /></Link>
        <Link to="/admin/settings/footer" className="rounded-xl bg-white/5 hover:bg-white/10 px-3.5 py-3 text-sm flex items-center justify-between">Footer <FiArrowRight aria-hidden="true" className="text-gray-500" /></Link>
      </div>
    </Panel>
  );
}

